<template>
  <v-dialog
    :value="value"
    max-width="960"
    persistent
    scrollable
    overlay-color="#000"
    overlay-opacity="0.72"
    content-class="mav-batch-dialog-shell"
    @input="$emit('input', $event)"
  >
    <div class="mav-batch-dialog">
      <!-- ─── Header ─── -->
      <div class="mav-batch-header">
        <div class="mav-batch-header-icon">
          <v-icon small color="white">mdi-robot</v-icon>
        </div>
        <div class="mav-batch-header-text">
          <div class="mav-batch-header-title">MAV Batch Classification</div>
          <div v-if="viewMode === 'input'" class="mav-batch-header-sub">
            {{ rows.length }} {{ rows.length === 1 ? 'domain' : 'domains' }} loaded from locked
            discoveries
          </div>
          <div v-else-if="viewMode === 'progress'" class="mav-batch-header-sub">
            {{ batchSucceeded + batchFailed }} of {{ batchTotal }} processed · {{ elapsedTime }}
          </div>
        </div>
        <v-btn icon small @click="close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- ─── INPUT VIEW ─── -->
      <template v-if="viewMode === 'input'">
        <!-- Toolbar (pinned above the scrollable list) -->
        <div v-if="isDeleteEnabled()" class="mav-batch-toolbar">
          <v-btn x-small text :disabled="rows.length >= MAX_BATCH_SIZE" @click="addRow">
            <v-icon x-small class="mr-1">mdi-plus</v-icon> Add domain
          </v-btn>
          <v-divider vertical class="mx-2" />
          <span class="mav-batch-toolbar-label">Default</span>
          <v-select
            v-model="defaultLocale"
            :items="localeOptions"
            dense
            hide-details
            solo
            flat
            background-color="transparent"
            class="mav-batch-default-locale"
          />
          <v-spacer />
          <span class="mav-batch-count">
            <strong>{{ rows.length }}</strong> / {{ MAX_BATCH_SIZE }}
          </span>
        </div>

        <div class="mav-batch-body">
          <!-- Resuming indicator -->
          <div v-if="resuming" class="mav-batch-resuming">
            <v-progress-circular indeterminate size="16" width="2" color="#764ba2" class="mr-2" />
            <span>Checking for in-flight batches…</span>
          </div>

          <!-- Recent completed batches (collapsible dropdown) -->
          <div v-if="recentBatches.length && !resuming" class="mav-batch-recent">
            <button class="mav-batch-recent-toggle" @click="recentExpanded = !recentExpanded">
              <v-icon x-small class="mr-1">{{
                recentExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
              <span class="mav-batch-recent-title">Recent batches</span>
              <v-chip x-small class="ml-2">{{ recentBatches.length }}</v-chip>
            </button>
            <div v-if="recentExpanded" class="mav-batch-recent-list">
              <div
                v-for="rb in recentBatches"
                :key="rb.batch_id"
                class="mav-batch-recent-row"
                @click="resumeCompletedBatch(rb)"
              >
                <v-chip x-small :color="batchStatusColor(rb.status)" dark class="mr-2">
                  {{ rb.status }}
                </v-chip>
                <span class="mav-batch-recent-meta">
                  {{ rb.total }} domains · {{ rb.succeeded }} ok, {{ rb.failed }} failed
                </span>
                <v-spacer />
                <span class="mav-batch-recent-time">{{ rb.submitted_at | time('short') }}</span>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="mav-batch-list">
            <div class="mav-batch-list-header">
              <div class="col-num">#</div>
              <div class="col-domain">Domain</div>
              <div class="col-locales">Locales</div>
              <div class="col-action"></div>
            </div>

            <div v-for="(row, index) in rows" :key="row.id" class="mav-batch-row">
              <div class="col-num">{{ index + 1 }}</div>
              <div class="col-domain">
                <input
                  v-model="row.domain"
                  :placeholder="row.domain ? '' : 'Enter domain (e.g. example.com)'"
                  class="mav-domain-input"
                  spellcheck="false"
                  autocapitalize="off"
                  autocomplete="off"
                  @keydown.enter="onDomainEnter(index)"
                />
              </div>
              <div class="col-locales">
                <v-menu offset-y :close-on-content-click="false" max-width="320">
                  <template #activator="{ on, attrs }">
                    <button class="mav-locale-button" v-bind="attrs" v-on="on">
                      <template v-if="row.locales.length">
                        <span v-for="loc in row.locales" :key="loc" class="mav-locale-chip">{{
                          loc
                        }}</span>
                      </template>
                      <span v-else class="mav-locale-empty">
                        {{ defaultLocale }} <span class="mav-locale-default-tag">(default)</span>
                      </span>
                      <v-icon x-small class="mav-locale-caret">mdi-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-card class="mav-locale-menu">
                    <div class="mav-locale-menu-title">
                      Locales for {{ row.domain || 'this row' }}
                    </div>
                    <v-combobox
                      v-model="row.locales"
                      :items="localeOptions"
                      placeholder="Type or select"
                      multiple
                      small-chips
                      deletable-chips
                      dense
                      outlined
                      hide-details
                      autofocus
                    />
                    <div class="mav-locale-menu-hint">
                      Leave empty to use default ({{ defaultLocale }})
                    </div>
                  </v-card>
                </v-menu>
              </div>
              <div v-if="isDeleteEnabled()" class="col-action">
                <v-btn icon x-small @click="removeRow(index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!rows.length" class="mav-batch-empty">
              <v-icon large class="mav-batch-empty-icon">mdi-inbox-outline</v-icon>
              <div>No domains to predict</div>
              <v-btn small text class="mt-2" @click="addRow">
                <v-icon small class="mr-1">mdi-plus</v-icon> Add one
              </v-btn>
            </div>
          </div>
        </div>
      </template>

      <!-- ─── PROGRESS VIEW ─── -->
      <div v-if="viewMode === 'progress'" class="mav-batch-body">
        <!-- Status strip -->
        <div class="mav-batch-status-strip">
          <v-chip :color="batchStatusColor(batchStatus)" dark small>
            {{ batchStatus | humanize }}
          </v-chip>
          <div class="mav-batch-stats">
            <span
              ><strong>{{ batchSucceeded }}</strong> done</span
            >
            <span v-if="batchFailed" class="error--text"
              ><strong>{{ batchFailed }}</strong> failed</span
            >
            <span class="text--secondary"
              >{{ batchTotal - batchSucceeded - batchFailed }} pending</span
            >
          </div>
          <v-spacer />
          <v-btn
            v-if="batchStatus === 'running' || batchStatus === 'queued'"
            x-small
            outlined
            color="error"
            @click="handleCancel"
          >
            <v-icon x-small class="mr-1">mdi-stop-circle</v-icon> Cancel
          </v-btn>
        </div>

        <v-progress-linear
          :value="progressPercent"
          :indeterminate="batchStatus === 'queued'"
          height="3"
          color="#764ba2"
          background-color="rgba(255,255,255,0.04)"
        />

        <!-- Live rows -->
        <div class="mav-batch-list mav-batch-list--progress">
          <div class="mav-batch-list-header">
            <div class="col-num">#</div>
            <div class="col-domain">Domain</div>
            <div class="col-status">Status</div>
            <div class="col-hierarchy">Parent → Brand → Channel</div>
            <div class="col-row-action"></div>
          </div>
          <template v-for="item in progressItems">
            <div
              :key="'row-' + item.index"
              class="mav-batch-row"
              :class="{ 'mav-batch-row--failed': item.status === 'failed' }"
            >
              <div class="col-num">{{ item.index + 1 }}</div>
              <div class="col-domain">
                <a :href="'http://' + item.domain" target="_blank" class="mav-domain-link">
                  {{ item.domain }}
                </a>
                <template v-if="item.resolvedDomain && item.resolvedDomain !== item.domain">
                  <v-icon x-small class="mav-resolved-arrow">mdi-arrow-right</v-icon>
                  <a
                    :href="'http://' + item.resolvedDomain"
                    target="_blank"
                    class="mav-resolved-domain"
                    >{{ item.resolvedDomain }}</a
                  >
                </template>
              </div>
              <div class="col-status">
                <v-progress-circular
                  v-if="item.status === 'running'"
                  indeterminate
                  size="12"
                  width="2"
                  color="#764ba2"
                />
                <v-icon v-else-if="item.status === 'completed'" x-small color="success"
                  >mdi-check-circle</v-icon
                >
                <v-icon v-else-if="item.status === 'failed'" x-small color="error"
                  >mdi-alert-circle</v-icon
                >
                <v-icon v-else-if="item.status === 'canceled'" x-small color="warning"
                  >mdi-cancel</v-icon
                >
                <v-icon v-else x-small color="grey">mdi-circle-outline</v-icon>
                <span class="ml-2">{{ item.status | humanize }}</span>
              </div>
              <div class="col-hierarchy">
                <template v-if="item.prediction && isInvalidPrediction(item)">
                  <span class="mav-hier-node error--text">INVALID</span>
                </template>
                <template v-else-if="item.prediction">
                  <span class="mav-hier-node">{{ item.prediction.parent_name || '—' }}</span>
                  <v-icon x-small class="mav-hier-sep">mdi-chevron-right</v-icon>
                  <span class="mav-hier-node">{{ item.prediction.brand_name || '—' }}</span>
                  <v-icon x-small class="mav-hier-sep">mdi-chevron-right</v-icon>
                  <span class="mav-hier-node mav-hier-node--leaf">{{
                    item.prediction.channel_name || '—'
                  }}</span>
                  <v-chip
                    v-if="item.prediction.overall_confidence"
                    x-small
                    :color="confidenceColor(item.prediction.overall_confidence)"
                    dark
                    class="ml-2 mav-confidence-chip"
                  >
                    {{ Math.round(item.prediction.overall_confidence * 100) }}%
                  </v-chip>
                </template>
                <span v-else class="text--secondary">—</span>
              </div>
              <div class="col-row-action">
                <v-btn
                  v-if="
                    isInsightsEnabled() &&
                      item.prediction &&
                      item.prediction.prediction_insight &&
                      item.status === 'completed'
                  "
                  x-small
                  text
                  :color="insightOpenIndex === item.index ? '#ce93d8' : '#78909c'"
                  class="mav-insight-btn"
                  @click="toggleInsight(item.index)"
                >
                  <v-icon x-small class="mr-1">mdi-lightbulb-on-outline</v-icon>
                  Insights
                </v-btn>
                <a
                  v-if="item._ruleCreated && item._ruleId"
                  class="mav-status-text success--text mav-proposal-link"
                  :href="`/cls/domain/rules?search=${item.domain}`"
                  target="_blank"
                >
                  Rule ↗
                </a>
                <a
                  v-else-if="item._proposalCreated && item._proposalId"
                  class="mav-status-text success--text mav-proposal-link"
                  :href="`/cls/domain/reviews?search=${item.domain}`"
                  target="_blank"
                >
                  Proposed ↗
                </a>
                <span v-else-if="item._proposalCreated" class="mav-status-text success--text">
                  Proposed
                </span>
                <v-progress-circular
                  v-else-if="item._proposalLoading"
                  indeterminate
                  size="16"
                  width="2"
                  color="success"
                />
                <v-btn
                  v-else-if="canShowProgressPropose(item)"
                  x-small
                  text
                  color="success"
                  @click="createProposalDuringProgress(item)"
                >
                  Propose
                </v-btn>
              </div>
            </div>
            <!-- Insights expandable panel -->
            <div
              v-if="
                isInsightsEnabled() &&
                  insightOpenIndex === item.index &&
                  item.prediction &&
                  item.prediction.prediction_insight
              "
              :key="'insight-' + item.index"
              class="mav-insight-panel"
            >
              <div class="mav-insight-panel-header">
                <v-icon small color="#ce93d8">mdi-lightbulb-on-outline</v-icon>
                <span class="mav-insight-panel-title">Prediction Insights</span>
                <v-chip
                  v-if="item.prediction.overall_confidence"
                  x-small
                  :color="confidenceColor(item.prediction.overall_confidence)"
                  dark
                  class="ml-auto"
                >
                  {{ confidenceLabel(item.prediction.overall_confidence) }}
                </v-chip>
                <v-btn icon x-small class="ml-2" @click="toggleInsight(-1)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="mav-insight-panel-body"
                v-html="renderInsightMarkdown(item.prediction.prediction_insight)"
              ></div>
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </template>
        </div>
      </div>

      <!-- ─── Footer ─── -->
      <div class="mav-batch-footer">
        <template v-if="viewMode === 'input'">
          <v-btn text :to="{ name: 'owl.domain.mav-batch.history' }">
            <v-icon small class="mr-1">mdi-history</v-icon> History
          </v-btn>
          <v-spacer />
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn
            :loading="submitting"
            :disabled="!validRows.length || submitting"
            class="mav-btn-primary"
            dark
            @click="submitBatch"
          >
            <v-icon small class="mr-1">mdi-robot</v-icon>
            Predict {{ validRows.length }} Domain{{ validRows.length !== 1 ? 's' : '' }}
          </v-btn>
        </template>

        <template v-if="viewMode === 'progress'">
          <v-btn text :to="{ name: 'owl.domain.mav-batch.history' }">
            <v-icon small class="mr-1">mdi-history</v-icon> History
          </v-btn>
          <v-spacer />
          <v-btn text @click="close">Close</v-btn>
        </template>
      </div>
    </div>

    <!-- Channel Suggestion Dialog -->
    <v-dialog
      v-model="channelSuggestionDialog"
      max-width="600px"
      persistent
      @click:outside="closeChannelSuggestion"
    >
      <brand-channel-form
        v-if="
          channelSuggestionItem &&
            channelSuggestionItem.prediction &&
            channelSuggestionItem.prediction.channel_suggestion
        "
        :value="channelSuggestionDialog"
        :channel="true"
        :post-endpoint="
          `/brand/${channelSuggestionItem.prediction.channel_suggestion.brand_id}/channel`
        "
        :prefill="getChannelPrefill(channelSuggestionItem)"
        :editing="null"
        :country-code-prop="'*'"
        cls
        @cancel="closeChannelSuggestion"
        @saved="onChannelCreated"
      />
    </v-dialog>

    <!-- Tree Creation Dialog -->
    <v-dialog
      v-model="treeCreationDialog"
      max-width="700px"
      persistent
      @click:outside="closeTreeCreation"
    >
      <v-card v-if="treeCreationItem" class="mav-tree-creation-card">
        <v-card-title class="mav-tree-creation-header">
          <v-icon class="mr-2" color="accent">mdi-file-tree</v-icon>
          Create New Hierarchy
          <v-spacer />
          <v-btn icon small @click="closeTreeCreation">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mav-tree-creation-body">
          <div class="mav-tree-creation-flow">
            <!-- Parent Step -->
            <div
              class="mav-tree-step"
              :class="{
                'mav-tree-step--active': treeCreationStep === 'parent',
                'mav-tree-step--completed': createdTreeIds.parent_id,
              }"
            >
              <div class="mav-tree-step-icon">
                <v-icon v-if="createdTreeIds.parent_id" small color="success"
                  >mdi-check-circle</v-icon
                >
                <v-progress-circular
                  v-else-if="treeCreationStep === 'parent' && treeCreationLoading"
                  indeterminate
                  size="16"
                  width="2"
                  color="accent"
                />
                <span v-else class="mav-tree-step-number">1</span>
              </div>
              <div class="mav-tree-step-content">
                <div class="mav-tree-step-label">Parent</div>
                <div class="mav-tree-step-value">
                  {{ treeCreationItem.prediction.parent_name || '—' }}
                </div>
              </div>
            </div>

            <v-icon class="mav-tree-arrow">mdi-chevron-right</v-icon>

            <!-- Brand Step -->
            <div
              class="mav-tree-step"
              :class="{
                'mav-tree-step--active': treeCreationStep === 'brand',
                'mav-tree-step--completed': createdTreeIds.brand_id,
              }"
            >
              <div class="mav-tree-step-icon">
                <v-icon v-if="createdTreeIds.brand_id" small color="success"
                  >mdi-check-circle</v-icon
                >
                <v-progress-circular
                  v-else-if="treeCreationStep === 'brand' && treeCreationLoading"
                  indeterminate
                  size="16"
                  width="2"
                  color="accent"
                />
                <span v-else class="mav-tree-step-number">2</span>
              </div>
              <div class="mav-tree-step-content">
                <div class="mav-tree-step-label">Brand</div>
                <div class="mav-tree-step-value">
                  {{ treeCreationItem.prediction.brand_name || '—' }}
                </div>
              </div>
            </div>

            <v-icon class="mav-tree-arrow">mdi-chevron-right</v-icon>

            <!-- Channel Step -->
            <div
              class="mav-tree-step"
              :class="{
                'mav-tree-step--active': treeCreationStep === 'channel',
                'mav-tree-step--completed': createdTreeIds.channel_id,
              }"
            >
              <div class="mav-tree-step-icon">
                <v-icon v-if="createdTreeIds.channel_id" small color="success"
                  >mdi-check-circle</v-icon
                >
                <v-progress-circular
                  v-else-if="treeCreationStep === 'channel' && treeCreationLoading"
                  indeterminate
                  size="16"
                  width="2"
                  color="accent"
                />
                <span v-else class="mav-tree-step-number">3</span>
              </div>
              <div class="mav-tree-step-content">
                <div class="mav-tree-step-label">Channel</div>
                <div class="mav-tree-step-value">
                  {{ treeCreationItem.prediction.channel_name || '—' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Status Messages -->
          <div v-if="treeCreationStep === 'checking'" class="mav-tree-status">
            <v-progress-circular indeterminate size="20" width="2" color="accent" class="mr-2" />
            <span>Checking proposal status...</span>
          </div>
          <div v-else-if="treeCreationStep" class="mav-tree-status">
            Creating {{ treeCreationStep }}...
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="treeCreationLoading" @click="closeTreeCreation">Cancel</v-btn>
          <v-btn
            v-if="!treeCreationStep"
            color="accent"
            :loading="treeCreationLoading"
            @click="startTreeCreation"
          >
            <v-icon small class="mr-1">mdi-play</v-icon>
            Start Creation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {
  submitAsyncBatch,
  getBatchStatus,
  connectBatchEvents,
  cancelBatch,
  listBatches,
  recordBatchItemEdit,
} from '@/api/mav-batch';
import { recordProposalSubmission } from '@/api/mav-batch-edits';
import { getConfigMap } from '@/helpers/filters';
import HTTP from '@/api/ant';
import OWL from '@/api/owl';
import BrandChannelForm from '@/components/mv/BrandChannelForm.vue';

const MAX_BATCH_SIZE = 50;
let rowIdCounter = 0;

export default {
  name: 'MavBatchDialog',

  components: {
    BrandChannelForm,
  },

  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    hasKnownInFlight: { type: Boolean, default: false },
  },

  data() {
    return {
      MAX_BATCH_SIZE,
      viewMode: 'input',
      rows: [],
      defaultLocale: 'us',
      submitting: false,
      resuming: false,
      recentExpanded: false,
      ignoreNotIdentifiable: false,
      ignoreInvalid: false,
      proposalsWithHierarchies: false,
      selectedIndices: [],

      localeOptions: [
        'us',
        'gb',
        'de',
        'fr',
        'jp',
        'au',
        'ca',
        'br',
        'in',
        'it',
        'es',
        'mx',
        'kr',
        'nl',
      ],

      activeBatchId: null,
      batchStatus: 'queued',
      batchTotal: 0,
      batchSucceeded: 0,
      batchFailed: 0,
      progressItems: [],
      eventStream: null,
      pollTimer: null,
      startTime: null,
      elapsedTimer: null,
      elapsedTime: '0s',

      resultItems: [],
      recentBatches: [],
      proposalAllLoading: false,

      // Channel suggestion dialog
      channelSuggestionDialog: false,
      channelSuggestionItem: null,
      channelCreationLoading: false,

      // Tree creation dialog
      treeCreationDialog: false,
      treeCreationItem: null,
      treeCreationStep: null, // 'parent', 'brand', 'channel', 'checking'
      treeCreationLoading: false,
      createdTreeIds: {}, // { parent_id, brand_id, channel_id, tree_proposal_id }

      // Prediction Insights
      insightOpenIndex: -1,
    };
  },

  computed: {
    validRows() {
      return this.rows.filter(r => r.domain && r.domain.trim());
    },
    isBatchTerminal() {
      return ['completed', 'failed', 'canceled', 'expired'].includes(this.batchStatus);
    },
    progressPercent() {
      if (!this.batchTotal) return 0;
      return ((this.batchSucceeded + this.batchFailed) / this.batchTotal) * 100;
    },
    selectableItems() {
      return this.filteredResultItems.filter(r => this.isSelectable(r));
    },
    selectedItems() {
      return this.filteredResultItems.filter(
        r => this.selectedIndices.includes(r.index) && r.success && r.prediction,
      );
    },
    selectedProposableItems() {
      return this.selectedItems.filter(r => r.discoveryId && !r._proposalCreated);
    },
    isAllSelected() {
      return (
        this.selectableItems.length > 0 &&
        this.selectableItems.every(r => this.selectedIndices.includes(r.index))
      );
    },
    isPartiallySelected() {
      if (this.isAllSelected) return false;
      return this.selectableItems.some(r => this.selectedIndices.includes(r.index));
    },
    filteredResultItems() {
      let items = this.resultItems;

      // Apply "Hide Not Identifiable" filter
      if (this.ignoreNotIdentifiable) {
        items = items.filter(r => !this.isNotIdentifiable(r));
      }

      // Apply "Hide Invalid" filter
      if (this.ignoreInvalid) {
        items = items.filter(r => !this.isInvalidPrediction(r));
      }

      // Apply "With Hierarchies" filter
      if (this.proposalsWithHierarchies) {
        items = items.filter(r => {
          if (!r.success || !r.prediction) return false;
          if (r.prediction.is_channel_suggestion || r.prediction.is_parent_suggestion) return false;
          if (this.isNotIdentifiable(r)) return false;
          if (this.isInvalidPrediction(r)) return false;
          return true;
        });
      }

      return items;
    },
  },

  watch: {
    value(isOpen) {
      if (isOpen) this.initFromItems();
      else this.cleanup();
    },
    batchStatus(newStatus) {
      this.$emit('batch-state', newStatus);

      if (this.isBatchTerminal && document.hidden) {
        this.notifyBatchComplete(newStatus);
      }
    },
    filteredResultItems() {
      // Clear selection when filters change to avoid stale indices
      this.selectedIndices = [];
    },
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    initFromItems() {
      if (this.hasKnownInFlight) {
        this.resumeExisting();
        return;
      }

      this.viewMode = 'input';
      this.activeBatchId = null;
      this.batchStatus = 'queued';
      this.batchTotal = 0;
      this.batchSucceeded = 0;
      this.batchFailed = 0;
      this.progressItems = [];
      this.resultItems = [];
      this.recentBatches = [];

      this.rows = this.items.slice(0, MAX_BATCH_SIZE).map(item => ({
        id: ++rowIdCounter,
        domain: item.domain,
        discoveryId: item.id || null,
        locales: Array.isArray(item.countries) ? [...item.countries] : [],
      }));

      // Check the server for in-flight or recently completed batches.
      // If one is still running, resume it automatically.
      this.checkExistingBatches();
    },

    async resumeExisting() {
      this.resuming = true;
      this.viewMode = 'progress';
      try {
        const batches = await listBatches();
        if (!Array.isArray(batches)) {
          this.viewMode = 'input';
          return;
        }
        const sorted = [...batches].sort(
          (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at),
        );
        const inFlight = sorted.find(b => b.status === 'queued' || b.status === 'running');
        if (inFlight) {
          await this.resumeBatch(inFlight);
        } else {
          // Batch must have just finished — show results of the most recent one.
          const recent = sorted[0];
          if (recent) {
            await this.resumeCompletedBatch(recent);
          } else {
            this.viewMode = 'input';
          }
        }
      } catch (err) {
        this.viewMode = 'input';
      } finally {
        this.resuming = false;
      }
    },

    async checkExistingBatches() {
      this.resuming = true;
      try {
        const batches = await listBatches();
        if (!Array.isArray(batches) || !batches.length) return;

        // Sort by submitted_at descending so the most recent is first.
        const sorted = [...batches].sort(
          (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at),
        );

        // Find the first in-flight batch (queued or running).
        const inFlight = sorted.find(b => b.status === 'queued' || b.status === 'running');

        // Keep recently completed batches for quick access.
        this.recentBatches = sorted
          .filter(b => b.status === 'completed' || b.status === 'failed')
          .slice(0, 5);

        if (inFlight) {
          await this.resumeBatch(inFlight);
        }
      } catch (err) {
        // Non-fatal — the user can still submit a new batch.
        // eslint-disable-next-line no-console
        console.warn('[MavBatchDialog] Failed to check existing batches:', err.message);
      } finally {
        this.resuming = false;
      }
    },

    isDeleteEnabled() {
      const config = this.getConfigMap();
      if (config['mavFeature_owl_ui']?.enabled?.v1)
        return config['mavFeature_owl_ui']?.enabled?.delete;
    },

    isDebugMode() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.debug || false;
    },

    isInsightsEnabled() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.insights || false;
    },

    async resumeBatch(batch) {
      this.activeBatchId = batch.batch_id;
      this.batchStatus = batch.status;
      this.batchTotal = batch.total || 0;
      this.batchSucceeded = batch.succeeded || 0;
      this.batchFailed = batch.failed || 0;

      // Try to restore the original domains from localStorage
      const storedDomains = this.getStoredBatchDomains(batch.batch_id);

      let batchStartTime = batch.started_at || batch.submitted_at;

      try {
        const full = await getBatchStatus(batch.batch_id);
        this.batchTotal = full.total || batch.total || 0;
        this.batchSucceeded = full.succeeded || 0;
        this.batchFailed = full.failed || 0;

        if (full.started_at) {
          batchStartTime = full.started_at;
        } else if (full.submitted_at) {
          batchStartTime = full.submitted_at;
        }

        this.progressItems = Array.from({ length: this.batchTotal }, (_, i) => {
          let domain = '';
          if (storedDomains && i < storedDomains.length) {
            domain = storedDomains[i];
          }
          return this.createEmptyProgressItem(i, domain);
        });

        if (full.results && full.results.length) {
          full.results.forEach(r => {
            let targetIndex = -1;

            if (r.domain) {
              targetIndex = this.progressItems.findIndex(p => p.domain === r.domain);
            }

            if (targetIndex !== -1) {
              this.progressItems[targetIndex] = this.buildProgressItemFromApiResult(
                this.progressItems[targetIndex],
                r,
              );
            } else if (r.domain) {
              const emptyIndex = this.progressItems.findIndex(p => !p.domain);
              if (emptyIndex !== -1) {
                this.progressItems[emptyIndex] = this.buildProgressItemFromApiResult(
                  this.progressItems[emptyIndex],
                  r,
                );
              }
            }
          });
        }

        if (full.domains && Array.isArray(full.domains)) {
          full.domains.forEach((domainInfo, i) => {
            if (i < this.progressItems.length && !this.progressItems[i].domain) {
              this.progressItems[i].domain =
                typeof domainInfo === 'string' ? domainInfo : domainInfo.domain;
            }
          });
        }
      } catch (err) {
        this.progressItems = Array.from({ length: this.batchTotal }, (_, i) => {
          let domain = '';
          if (storedDomains && i < storedDomains.length) {
            domain = storedDomains[i];
          }
          return this.createEmptyProgressItem(i, domain);
        });
      }

      this.viewMode = 'progress';
      this.startElapsedTimer(batchStartTime);

      // Reconnect to the SSE stream for live updates.
      await this.connectToEvents(batch.batch_id);
    },

    async resumeCompletedBatch(batch) {
      this.openResultsInNewTab(batch.batch_id);
      this.close();
    },

    openResultsInNewTab(batchId) {
      const route = this.$router.resolve({
        name: 'owl.domain.mav-batch.results',
        params: { batchId },
      });
      window.open(route.href, '_blank');
    },

    handleBatchComplete(batchId) {
      if (document.hidden) {
        this.notifyBatchComplete(this.batchStatus);
      }
      this.openResultsInNewTab(batchId);
      this.close();
    },

    createEmptyProgressItem(index, domain = '') {
      return {
        index,
        domain,
        discoveryId: this.findDiscoveryIdForDomain(domain),
        status: 'pending',
        prediction: null,
        resolvedDomain: null,
        validation: null,
        error: null,
        duration: null,
        edits: [],
        _proposalCreated: false,
        _proposalId: null,
        _proposalLoading: false,
      };
    },

    findDiscoveryIdForDomain(domain) {
      if (!domain) return null;
      const row = this.rows.find(r => r.domain === domain);
      if (row && row.discoveryId) return row.discoveryId;
      if (this.items && this.items.length) {
        const match = this.items.find(it => it.domain === domain);
        if (match) return match.id || match.discoveryId || null;
      }
      return null;
    },

    buildProgressItemFromApiResult(existing, apiResult) {
      const prediction = apiResult.prediction || null;
      const validation =
        prediction && prediction.validation_result ? prediction.validation_result : null;
      const domain = apiResult.domain || existing.domain;

      // Use the explicit status field from the API if available; fall back to
      // deriving from the boolean `success` field for backward compat.
      let status = apiResult.status;
      if (!status) {
        status = apiResult.success ? 'completed' : 'failed';
      }

      const incoming = {
        domain,
        discoveryId:
          existing.discoveryId ||
          apiResult.discovery_id ||
          apiResult.discoveryId ||
          this.findDiscoveryIdForDomain(domain),
        status,
        prediction,
        resolvedDomain:
          prediction && prediction.resolved_domain ? prediction.resolved_domain : null,
        validation,
        error:
          apiResult.error ||
          (validation && validation.is_valid === false
            ? this.formatValidationError(validation)
            : null),
        edits: apiResult.edits || existing.edits || [],
      };

      const merged = this.mergeProgressItemUpdate(existing, incoming);
      this.hydrateEditsFromResults([merged]);
      return merged;
    },

    mergeProgressItemUpdate(existing, incoming) {
      const merged = {
        ...existing,
        ...incoming,
        index: existing.index,
      };

      if (existing._proposalCreated) {
        merged._proposalCreated = true;
        merged._proposalId = existing._proposalId ?? incoming._proposalId ?? null;
      }

      if (existing._proposalLoading) {
        merged._proposalLoading = existing._proposalLoading;
      }

      merged.discoveryId =
        existing.discoveryId ||
        incoming.discoveryId ||
        this.findDiscoveryIdForDomain(merged.domain);

      if (incoming.edits && incoming.edits.length) {
        merged.edits = incoming.edits;
      } else {
        merged.edits = existing.edits || [];
      }

      return merged;
    },

    canShowProgressPropose(item) {
      if (!item || item._proposalCreated || item._proposalLoading) return false;
      // Edits are now allowed on items in any state — only require prediction data
      if (!item.prediction) return false;
      if (item.prediction.is_new_hierarchy !== false) return false;
      if (this._resolveTreeId(item.prediction) || this.isInvalidPrediction(item)) return true;
      return false;
    },

    async createProposalDuringProgress(item) {
      if (!item.prediction || item._proposalCreated || item._proposalLoading) return;
      if (!this.canShowProgressPropose(item)) return;

      const { tree_id, isInvalid, payload, endpoint } = this._buildMultipleRulesPayload(item);
      if (!tree_id && !isInvalid) {
        this.$flash(
          'error',
          `No valid tree ID for ${item.domain}. The predicted IDs may be invalid.`,
        );
        return;
      }

      this.$set(item, '_proposalLoading', true);
      try {
        const discoveryResponse = await HTTP.get('/domain/discoveries', {
          params: { search: item.domain },
        });

        if (!discoveryResponse.data || discoveryResponse.data.length === 0) {
          this.$flash('error', `Discovery not found for ${item.domain}`);
          return;
        }

        const discoveryId = discoveryResponse.data[0].id;
        const proposalResponse = await HTTP.post(
          `/domain/discoveries/${discoveryId}/${endpoint}`,
          payload,
          { niSkipFlash: true },
        );
        const proposalId = proposalResponse.data?.id || proposalResponse.data?.proposal_id || null;

        this.$set(item, '_proposalCreated', true);
        this.$set(item, '_proposalId', proposalId);
        this.$set(item, 'discoveryId', discoveryId);
        this.$set(this.progressItems, item.index, { ...item });

        this.$emit('remove-locked-row', discoveryId);

        if (this.activeBatchId && typeof item.index === 'number') {
          try {
            await recordProposalSubmission(this.activeBatchId, item.index, {
              tree_id,
              discovery_id: discoveryId,
              proposal_id: proposalId,
            });
          } catch (err) {
            console.warn('[MavBatchDialog] Failed to record proposal submission:', err.message);
          }
        }

        this.$flash('success', `Proposal created for ${item.domain}`);
      } catch (err) {
        if (err.response?.status === 400 && err.response?.data?.errors) {
          const key = Object.keys(err.response.data.errors)[0];
          this.$flash('error', `${key} ${err.response.data.errors[key]}`);
        } else {
          this.$flash('error', `Failed to create proposal for ${item.domain}`);
        }
      } finally {
        this.$set(item, '_proposalLoading', false);
        this.$set(this.progressItems, item.index, { ...item });
      }
    },

    close() {
      this.cleanup();
      this.$emit('input', false);
    },

    cleanup() {
      if (this.eventStream) {
        this.eventStream.close();
        this.eventStream = null;
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },

    addRow() {
      if (this.rows.length >= MAX_BATCH_SIZE) return;
      this.rows.push({ id: ++rowIdCounter, domain: '', locales: [] });
      // Focus the new row's input after it renders
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.mav-domain-input');
        const last = inputs[inputs.length - 1];
        if (last) last.focus();
      });
    },

    onDomainEnter(index) {
      // Enter on the last row adds another empty row (unless we're at the cap)
      if (index === this.rows.length - 1 && this.rows[index].domain.trim()) {
        this.addRow();
      }
    },

    removeRow(index) {
      this.rows.splice(index, 1);
    },

    async submitBatch() {
      const domains = this.validRows.map(row => ({
        domain: row.domain.trim(),
        countries: row.locales.length ? row.locales.join(',') : this.defaultLocale,
      }));

      if (!domains.length) return;

      this.submitting = true;
      try {
        await this.submitAsync(domains);
      } catch (err) {
        if (err && err.status === 429) {
          const wait = err.retryAfter || 60;
          this.$flash('warning', `Too many concurrent batches. Try again in ${wait}s.`);
        } else if (err && err.status === 409) {
          this.$flash(
            'error',
            'This batch was already submitted with different contents. Please retry.',
          );
        } else {
          this.$flash('error', (err && err.message) || 'Batch submission failed');
        }
      } finally {
        this.submitting = false;
      }
    },

    async submitAsync(domains) {
      // Idempotency-Key lets the server dedupe safe retries on network flakes.
      const idempotencyKey = this.buildIdempotencyKey();
      const result = await submitAsyncBatch(domains, idempotencyKey);
      this.activeBatchId = result.batch_id;
      this.batchStatus = result.status;
      this.batchTotal = result.total;
      this.batchSucceeded = 0;
      this.batchFailed = 0;

      // Store the original domains in localStorage so we can restore them on reopen
      this.storeBatchDomains(result.batch_id, domains);

      // Ask for notification permission now — the user just kicked off a
      // potentially long-running operation and will want to know when it's done.
      this.requestNotificationPermission();

      // The server may silently drop per-item validation failures (empty
      // domain, oversized strings), so `result.total` can be smaller than
      // the number of domains we sent. Build the progress list from the
      // domains we submitted, then trim/align it later using item events
      // (which carry `domain`) and the final status payload.
      this.progressItems = domains.map((d, i) => this.createEmptyProgressItem(i, d.domain));

      if (result.total < domains.length) {
        // Flag the overflow rows as dropped so the user can see why the
        // server's count is smaller than what they submitted.
        this.progressItems.slice(result.total).forEach((item, offset) => {
          const idx = result.total + offset;
          this.$set(this.progressItems, idx, {
            ...item,
            status: 'failed',
            error: 'dropped by server validation',
          });
        });
        this.batchFailed = domains.length - result.total;
      }

      this.viewMode = 'progress';
      this.startElapsedTimer();
      await this.connectToEvents(result.batch_id);
    },

    async connectToEvents(batchId) {
      try {
        this.eventStream = await connectBatchEvents(batchId);

        this.eventStream.onStatus(raw => {
          // The server wraps events as `{source, type, data}`; the useful
          // payload lives under `.data`. Accept the flat shape too so this
          // keeps working against test doubles or a future flattening.
          const data = raw && raw.data && typeof raw.data === 'object' ? raw.data : raw;
          if (!data || !data.status) return;

          this.batchStatus = data.status;
          if (data.succeeded !== undefined) this.batchSucceeded = data.succeeded;
          if (data.failed !== undefined) this.batchFailed = data.failed;

          if (this.isBatchTerminal) {
            this.cleanup();
            this.stopElapsedTimer();
            this.handleBatchComplete(batchId);
          }
        });

        this.eventStream.onItem(raw => {
          const data = raw && raw.data && typeof raw.data === 'object' ? raw.data : raw;
          if (!data || !data.domain) return;

          const target = this.findProgressItem(data);
          if (!target) return;

          const { item, index } = target;
          const previousStatus = item.status;

          // When auto_create is enabled, a second item event may arrive for
          // the same index with updated prediction data (after the retry
          // succeeds). Always accept newer prediction data — replace rather
          // than skip duplicate index events.
          if (item.prediction && !data.prediction) {
            return;
          }

          const merged = this.buildProgressItemFromApiResult(item, {
            domain: data.domain,
            status: data.status,
            success: data.status === 'completed',
            prediction: data.prediction || null,
            error: data.error || null,
            edits: data.edits,
            discovery_id: data.discovery_id || data.discoveryId || null,
            is_parent_suggestion: data.is_parent_suggestion,
            is_brand_suggestion: data.is_brand_suggestion,
            is_channel_suggestion: data.is_channel_suggestion,
            channel_suggestion: data.channel_suggestion || null,
          });

          this.$set(this.progressItems, index, merged);

          const newStatus = merged.status;

          // Only adjust counters when transitioning from a non-terminal state,
          // or when replacing a failed result with a successful retry.
          if (previousStatus !== 'completed' && previousStatus !== 'failed') {
            if (newStatus === 'completed') this.batchSucceeded += 1;
            else if (newStatus === 'failed') this.batchFailed += 1;
          } else if (previousStatus === 'failed' && newStatus === 'completed') {
            // auto_create retry turned a failure into success
            this.batchFailed -= 1;
            this.batchSucceeded += 1;
          }
        });

        this.eventStream.onError(() => this.startPolling(batchId));
      } catch (err) {
        this.startPolling(batchId);
      }
    },

    formatValidationError(validation) {
      if (!validation || validation.is_valid !== false) return '';
      const kind = validation.validation_type || 'invalid';
      const details = validation.details || {};
      if (details.status_code) {
        if (details.status_code === 200 && kind === 'under_construction') {
          return 'N/A';
        }
        return `${kind} (HTTP ${details.status_code})`;
      }
      if (details.error_message) {
        const msg = String(details.error_message);
        if (/no such host|lookup/i.test(msg)) return `${kind} (DNS)`;
        return `${kind}: ${msg.slice(0, 80)}`;
      }
      return kind;
    },

    /**
     * Check if a result item represents a NOT_IDENTIFIABLE outcome.
     * The server may signal this via:
     *  - prediction.outcome === 'not_identifiable'
     *  - error string containing 'not_identifiable' or 'NOT_IDENTIFIABLE'
     *  - prediction with all zero/null IDs (no parent, brand, or channel)
     *  - prediction with hierarchy names containing 'NOT IDENTIFIABLE'
     */
    isNotIdentifiable(item) {
      if (!item) return false;
      const prediction = item.prediction || (item.data && item.data.prediction);
      if (!prediction) return false;
      if (prediction.outcome && /not.?identifiable/i.test(prediction.outcome)) {
        return true;
      }
      if (item.error && /not.?identifiable/i.test(item.error)) {
        return true;
      }
      // Check if hierarchy names contain "NOT IDENTIFIABLE"
      const names = [prediction.parent_name, prediction.brand_name, prediction.channel_name];
      if (names.some(n => n && /not.?identifiable/i.test(n))) {
        return true;
      }
      // Treat results with no valid hierarchy IDs as not identifiable
      if (!prediction.parent_id && !prediction.brand_id && !prediction.channel_id) {
        return true;
      }
      return false;
    },

    /**
     * Check if a result item represents an INVALID prediction.
     * The server signals this when hierarchy names are "INVALID" or the
     * outcome field indicates invalidity.
     */
    isInvalidPrediction(item) {
      if (!item) return false;
      const prediction = item.prediction || (item.data && item.data.prediction);
      if (!prediction) return false;
      if (prediction.outcome && /^invalid$/i.test(prediction.outcome)) {
        return true;
      }
      // Check if all hierarchy names are "INVALID"
      const names = [prediction.parent_name, prediction.brand_name, prediction.channel_name];
      const nonEmpty = names.filter(n => n && n.trim());
      if (nonEmpty.length > 0 && nonEmpty.every(n => /^invalid$/i.test(n))) {
        return true;
      }
      return false;
    },

    // Match an item event or poll result row to a client-side progress row.
    findProgressItem(row) {
      if (!row) return null;

      if (row.domain) {
        const byDomain = this.progressItems.findIndex(p => p.domain === row.domain);
        if (byDomain !== -1) {
          return { item: this.progressItems[byDomain], index: byDomain };
        }
      }

      if (Number.isInteger(row.index) && row.index >= 0 && row.index < this.progressItems.length) {
        const item = this.progressItems[row.index];
        // Only use index if the domain matches or slot is empty
        if (!item.domain || item.domain === row.domain) {
          return { item, index: row.index };
        }
      }

      // Last resort: find first empty slot (only for new domains we don't know about)
      if (row.domain) {
        const emptySlot = this.progressItems.findIndex(p => !p.domain);
        if (emptySlot !== -1) {
          return { item: this.progressItems[emptySlot], index: emptySlot };
        }
      }

      return null;
    },

    startPolling(batchId) {
      if (this.eventStream) {
        this.eventStream.close();
        this.eventStream = null;
      }

      this.pollTimer = setInterval(async () => {
        try {
          const data = await getBatchStatus(batchId);
          this.batchStatus = data.status;
          this.batchSucceeded = data.succeeded || 0;
          this.batchFailed = data.failed || 0;

          if (Array.isArray(data.results)) {
            data.results.forEach(r => {
              const target = this.findProgressItem(r);
              if (!target) return;
              const { item, index } = target;
              const merged = this.buildProgressItemFromApiResult(item, r);
              this.$set(this.progressItems, index, merged);
            });
          }

          if (this.isBatchTerminal) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.stopElapsedTimer();
            this.handleBatchComplete(batchId);
          }
        } catch (err) {
          // Retry next tick
        }
      }, 3000);
    },

    // Stable-ish key for retry-safe async submits. The server uses this to
    // replay the original batch_id when the same payload is re-submitted,
    // or 409 when the key is reused with a different payload.
    buildIdempotencyKey() {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `mav-batch-${crypto.randomUUID()}`;
      }
      return `mav-batch-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    },

    // ─── Browser Notifications ───

    requestNotificationPermission() {
      if (!('Notification' in window)) return;
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    },

    notifyBatchComplete(status) {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const title =
        status === 'completed'
          ? 'MAV Batch Complete'
          : `MAV Batch ${status.charAt(0).toUpperCase() + status.slice(1)}`;

      const body =
        status === 'completed'
          ? `${this.batchSucceeded} of ${this.batchTotal} domains classified successfully.`
          : `Batch finished with status: ${status}. ${this.batchSucceeded} succeeded, ${this.batchFailed} failed.`;

      const notification = new Notification(title, {
        body,
        icon: '/favicons/nielsen-icon.svg',
        tag: `mav-batch-${this.activeBatchId}`,
      });

      // Clicking the notification opens the batch results page.
      notification.onclick = () => {
        window.focus();
        if (this.activeBatchId) {
          this.openResultsInNewTab(this.activeBatchId);
        }
        notification.close();
      };
    },

    async handleCancel() {
      if (!this.activeBatchId) return;

      try {
        await this.$root.$confirm(
          'Cancel Batch',
          'Cancel this batch? In-flight predictions will be stopped.',
        );
      } catch (err) {
        return;
      }

      try {
        await cancelBatch(this.activeBatchId);

        this.batchStatus = 'canceled';

        this.cleanup();
        this.stopElapsedTimer();

        this.progressItems = this.progressItems.map(item => {
          if (item.status === 'pending' || item.status === 'running') {
            return {
              ...item,
              status: 'failed',
              error: 'Batch canceled by user',
            };
          }
          return item;
        });

        this.$flash('success', 'Batch canceled');
      } catch (err) {
        this.$flash('error', err.message || 'Failed to cancel batch');
      }
    },

    applyOne(item) {
      const prediction = { ...item.prediction };

      // Tag invalid predictions so ClassifyRow toggles the valid/invalid state
      if (this.isInvalidPrediction(item)) {
        prediction._isInvalid = true;
      }

      this.$emit('apply-prediction', {
        domain: item.domain,
        prediction,
      });
      this.$set(item, '_predictionAccepted', true);
      this.$flash('success', `Applied prediction for ${item.domain}`);
    },

    // ─── Selection Methods ───

    isSelectable(item) {
      return item.success && item.prediction && !item._proposalCreated && !item._predictionAccepted;
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedIndices = this.selectableItems.map(r => r.index);
      } else {
        this.selectedIndices = [];
      }
    },

    toggleSelect(item) {
      const idx = this.selectedIndices.indexOf(item.index);
      if (idx !== -1) {
        this.selectedIndices.splice(idx, 1);
      } else {
        this.selectedIndices.push(item.index);
      }
    },

    applySelected() {
      this.selectedItems.forEach(item => {
        const prediction = { ...item.prediction };

        if (this.isInvalidPrediction(item)) {
          prediction._isInvalid = true;
        }

        this.$emit('apply-prediction', {
          domain: item.domain,
          prediction,
        });
        this.$set(item, '_predictionAccepted', true);
      });
      this.$flash('success', `Applied ${this.selectedItems.length} predictions`);
      this.selectedIndices = [];
    },

    async createProposalSelected() {
      this.proposalAllLoading = true;
      let succeeded = 0;
      let failed = 0;

      for (const item of this.selectedProposableItems) {
        if (item._proposalCreated) {
          succeeded++;
          continue;
        }

        if (!item.discoveryId) {
          failed++;
          continue;
        }

        try {
          const { tree_id, isInvalid, payload, endpoint } = this._buildMultipleRulesPayload(item);

          // Invalid entries can be proposed without a tree_id
          if (!tree_id && !isInvalid) {
            failed++;
            continue;
          }

          const prediction = item.prediction;
          const response = await HTTP.post(
            `/domain/discoveries/${item.discoveryId}/${endpoint}`,
            payload,
            { niSkipFlash: true },
          );
          this.$set(item, '_proposalCreated', true);
          this.$set(item, '_proposalId', proposalId);
          succeeded++;

          if (item.discoveryId) {
            this.$emit('remove-locked-row', item.discoveryId);
          }

          const proposalId = response.data?.id || response.data?.proposal_id || tree_id;
          const proposalType = prediction.channel_id
            ? 'channel'
            : prediction.brand_id
            ? 'brand'
            : 'parent';
          this.recordAction(item, `${proposalType}_created`, {
            proposal_id: proposalId,
            timestamp: new Date().toISOString(),
          });
        } catch {
          failed++;
        }
      }

      this.proposalAllLoading = false;
      this.selectedIndices = [];
      if (failed === 0) {
        this.$flash('success', `Created ${succeeded} proposals successfully`);
      } else {
        this.$flash('warning', `Created ${succeeded} proposals, ${failed} failed`);
      }
    },

    async createProposalOne(item) {
      if (!item.prediction) return;
      if (!item.discoveryId) {
        this.$flash(
          'error',
          `Cannot create proposal for ${item.domain}: discovery ID is not available. ` +
            'This batch was resumed from history without the original discovery context.',
        );
        return;
      }

      const { tree_id, isInvalid, payload } = this._buildMultipleRulesPayload(item);

      // Invalid entries can be proposed without a tree_id (using is_valid: false)
      if (!tree_id && !isInvalid) {
        this.$flash(
          'error',
          `No valid tree ID for ${item.domain}. The predicted IDs may be invalid.`,
        );
        return;
      }

      this.$set(item, '_proposalLoading', true);
      try {
        const prediction = item.prediction;
        const response = await HTTP.post(
          `/domain/discoveries/${item.discoveryId}/multiplerules`,
          payload,
          { niSkipFlash: true },
        );
        this.$flash('success', `Proposal created for ${item.domain}`);
        this.$set(item, '_proposalCreated', true);
        this.$set(item, '_proposalId', proposalId);

        // Remove from locked classify rows
        this.$emit('remove-locked-row', item.discoveryId);

        // Record proposal_submitted edit
        const proposalId = response.data?.id || response.data?.proposal_id || tree_id;
        const proposalType = prediction.channel_id
          ? 'channel'
          : prediction.brand_id
          ? 'brand'
          : 'parent';
        this.recordAction(item, `${proposalType}_created`, {
          proposal_id: proposalId,
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        if (err.response?.status === 400 && err.response?.data?.errors) {
          const key = Object.keys(err.response.data.errors)[0];
          this.$flash('error', `${key} ${err.response.data.errors[key]}`);
        } else {
          this.$flash('error', `Failed to create proposal for ${item.domain}`);
        }
      } finally {
        this.$set(item, '_proposalLoading', false);
      }
    },

    exportResults() {
      const header =
        'domain,status,parent_name,parent_id,brand_name,brand_id,channel_name,channel_id,category,confidence,error';
      const csvRows = this.resultItems.map(item => {
        const p = item.prediction || {};
        return [
          item.domain,
          item.success ? 'success' : 'failed',
          p.parent_name || '',
          p.parent_id || '',
          p.brand_name || '',
          p.brand_id || '',
          p.channel_name || '',
          p.channel_id || '',
          p.category_name || '',
          p.overall_confidence || '',
          item.error || '',
        ]
          .map(v => `"${String(v).replace(/"/g, '""')}"`)
          .join(',');
      });

      const csv = [header, ...csvRows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mav-batch-${this.activeBatchId || Date.now()}.csv`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    },

    startElapsedTimer(batchStartTime) {
      if (batchStartTime) {
        try {
          this.startTime = new Date(batchStartTime).getTime();
        } catch (err) {
          this.startTime = Date.now();
        }
      } else {
        this.startTime = Date.now();
      }

      this.elapsedTimer = setInterval(() => {
        const seconds = Math.floor((Date.now() - this.startTime) / 1000);
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        this.elapsedTime = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      }, 1000);
    },

    stopElapsedTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },

    confidenceColor(confidence) {
      const c = confidence || 0;
      if (c >= 0.9) return '#43a047';
      if (c >= 0.7) return '#fb8c00';
      if (c >= 0.5) return '#e65100';
      return '#c62828';
    },

    batchStatusColor(status) {
      const map = {
        queued: 'grey',
        running: 'accent',
        completed: 'success',
        failed: 'error',
        canceled: 'warning',
        expired: 'grey darken-1',
      };
      return map[status] || 'grey';
    },

    // ─── LocalStorage helpers for persisting batch domains ───

    storeBatchDomains(batchId, domains) {
      try {
        const key = `mav-batch-domains-${batchId}`;
        const domainStrings = domains
          .map(d => {
            if (typeof d === 'string') return d;
            if (d && d.domain) return d.domain;
            return '';
          })
          .filter(d => d);

        const data = {
          batchId,
          domains: domainStrings,
          timestamp: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(data));

        this.cleanupOldBatchData();
      } catch (err) {
        console.warn('[MavBatchDialog] Failed to store batch domains:', err);
      }
    },

    getStoredBatchDomains(batchId) {
      try {
        const key = `mav-batch-domains-${batchId}`;
        const stored = localStorage.getItem(key);
        if (!stored) return null;

        const data = JSON.parse(stored);
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - data.timestamp > sevenDays) {
          localStorage.removeItem(key);
          return null;
        }

        return data.domains;
      } catch (err) {
        console.warn('[MavBatchDialog] Failed to retrieve batch domains:', err);
        return null;
      }
    },

    cleanupOldBatchData() {
      try {
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        const keysToRemove = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('mav-batch-domains-')) {
            try {
              const stored = localStorage.getItem(key);
              const data = JSON.parse(stored);
              if (Date.now() - data.timestamp > sevenDays) {
                keysToRemove.push(key);
              }
            } catch (e) {
              keysToRemove.push(key);
            }
          }
        }

        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (err) {
        console.warn('[MavBatchDialog] Failed to cleanup old batch data:', err);
      }
    },

    // ─── Channel Suggestion Methods ───

    openChannelSuggestion(item) {
      this.channelSuggestionItem = item;
      this.channelSuggestionDialog = true;
      this.$set(item, '_channelLoading', true);
    },

    closeChannelSuggestion() {
      if (this.channelSuggestionItem) {
        this.$set(this.channelSuggestionItem, '_channelLoading', false);
      }
      this.channelSuggestionDialog = false;
      this.channelSuggestionItem = null;
    },

    getChannelPrefill(item) {
      if (!item || !item.prediction || !item.prediction.channel_suggestion) {
        return {};
      }

      const suggestion = item.prediction.channel_suggestion;
      return {
        name: suggestion.suggested_name || '',
        brand_id: suggestion.brand_id || null,
        brand_name: suggestion.brand_name || '',
        ad_supported: suggestion.ad_supported || 'unknown',
        sub_category_id: suggestion.subcategory_id || null,
        sub_category_name: suggestion.subcategory_name || '',
        category_id: suggestion.category_id || null,
        category_name: suggestion.category_name || '',
        status: 'active',
        country_codes: ['*'],
      };
    },

    async onChannelCreated(savedPayload) {
      const item = this.channelSuggestionItem;
      if (!item) return;

      this.closeChannelSuggestion();

      // FormMixin emits { current: axiosResponse, previous }
      // The API response shape: { id (proposal), status, item: { id, name, ... }, ... }
      const response = savedPayload.current ? savedPayload.current.data : savedPayload;
      const channelEntity = response.item || response;
      const proposalStatus = response.status;
      const channelId = channelEntity.id || 0;
      const channelName = channelEntity.name || '';

      // Check the status of the created channel
      if (proposalStatus === 'approved') {
        // Channel is approved, show apply and propose buttons
        this.$set(item, '_channelCreated', true);
        this.$set(item.prediction, 'channel_id', channelId);
        this.$set(item.prediction, 'channel_name', channelName);
        // Clear channel_suggestion flag so Apply/Propose buttons show
        this.$set(item.prediction, 'is_channel_suggestion', false);
        this.$flash('success', `Channel "${channelName}" created and approved`);
      } else {
        // Channel is pending approval — persist the pending state and record the channel ID
        // so we don't lose track of the API call that was already made
        this.$set(item, '_channelProposalPending', true);
        this.$set(item, '_channelCreated', true);
        this.$set(item.prediction, 'channel_id', channelId);
        this.$set(item.prediction, 'channel_name', channelName);
        this.$flash('info', `Channel "${channelName}" created but pending approval`);
      }

      // Record channel_created edit (persists for both approved and pending)
      this.recordAction(item, 'channel_created', {
        proposal_id: channelId,
        timestamp: new Date().toISOString(),
      });
    },

    // ─── Tree Creation Methods ───

    openTreeCreation(item) {
      this.treeCreationItem = item;
      this.treeCreationDialog = true;
      this.treeCreationStep = null;
      this.createdTreeIds = {};
      this.$set(item, '_treeLoading', true);
    },

    closeTreeCreation() {
      if (this.treeCreationItem) {
        this.$set(this.treeCreationItem, '_treeLoading', false);
      }
      this.treeCreationDialog = false;
      this.treeCreationItem = null;
      this.treeCreationStep = null;
      this.createdTreeIds = {};
      this.treeCreationLoading = false;
    },

    async startTreeCreation() {
      const item = this.treeCreationItem;
      if (!item || !item.prediction) return;

      const prediction = item.prediction;

      try {
        // Step 1: Create Parent
        this.treeCreationStep = 'parent';
        this.treeCreationLoading = true;

        const parentPayload = {
          parent: {
            name: prediction.parent_name || '',
            country_codes: ['*'],
            start_date: null,
          },
          reason: 'Created by MAV',
          created_via_mav: true,
        };

        const parentResponse = await OWL.post('/proposals/tree', parentPayload, {
          niSkipFlash: true,
        });

        this.createdTreeIds.parent_id = parentResponse.data.item?.id || parentResponse.data.id;
        this.createdTreeIds.tree_proposal_id = parentResponse.data.id;

        // Step 2: Create Brand
        this.treeCreationStep = 'brand';

        const brandPayload = {
          name: prediction.brand_name || '',
          parent_id: this.createdTreeIds.parent_id,
          ad_supported: 'unknown',
          sub_category_id: prediction.category_id || null,
          status: 'active',
          country_codes: ['*'],
        };

        const brandResponse = await OWL.post(
          `/proposed/parents/${this.createdTreeIds.parent_id}/brands`,
          brandPayload,
          { niSkipFlash: true },
        );

        this.createdTreeIds.brand_id = brandResponse.data.item?.id || brandResponse.data.id;

        // Step 3: Create Channel — use the freshly created brand_id, not prediction.brand_id
        this.treeCreationStep = 'channel';

        const channelPayload = {
          name: prediction.channel_name || '',
          brand_id: this.createdTreeIds.brand_id,
          ad_supported: 'unknown',
          sub_category_id: prediction.category_id || null,
          status: 'active',
          country_codes: ['*'],
        };

        const channelResponse = await OWL.post(
          `/proposed/brands/${this.createdTreeIds.brand_id}/channels`,
          channelPayload,
          { niSkipFlash: true },
        );

        this.createdTreeIds.channel_id = channelResponse.data.item?.id || channelResponse.data.id;

        // Step 4: Check proposal status
        this.treeCreationStep = 'checking';

        const statusResponse = await OWL.get(
          `/proposals/tree/${this.createdTreeIds.tree_proposal_id}`,
        );

        const proposalStatus = statusResponse.data.status;

        if (proposalStatus === 'approved') {
          // Approved - update prediction IDs with the *newly created* IDs
          this.$set(item, '_treeCreated', true);
          this.$set(item.prediction, 'parent_id', this.createdTreeIds.parent_id);
          this.$set(item.prediction, 'brand_id', this.createdTreeIds.brand_id);
          this.$set(item.prediction, 'channel_id', this.createdTreeIds.channel_id);
          // Clear is_parent_suggestion so Apply/Propose buttons show
          this.$set(item.prediction, 'is_parent_suggestion', false);
          this.$flash('success', 'Hierarchy created and approved');
        } else {
          // Pending approval — still record what we created
          this.$set(item, '_treeProposalPending', true);
          this.$set(item, '_treeCreated', true);
          this.$flash('info', 'Hierarchy created but pending approval');
        }

        // Record hierarchy_created edit for persistence
        this.recordAction(item, 'hierarchy_created', {
          parent_id: this.createdTreeIds.parent_id,
          parent_name: prediction.parent_name ?? '',
          brand_id: this.createdTreeIds.brand_id,
          brand_name: prediction.brand_name ?? '',
          channel_id: this.createdTreeIds.channel_id ?? null,
          channel_name:
            this.createdTreeIds.channel_id != null ? prediction.channel_name ?? '' : null,
          tree_proposal_id: this.createdTreeIds.tree_proposal_id ?? null,
          timestamp: new Date().toISOString(),
        });

        this.closeTreeCreation();
      } catch (err) {
        this.treeCreationLoading = false;
        if (err.response?.status === 400 && err.response?.data?.errors) {
          const key = Object.keys(err.response.data.errors)[0];
          this.$flash('error', `${key}: ${err.response.data.errors[key]}`);
        } else {
          this.$flash(
            'error',
            `Failed to create ${this.treeCreationStep}: ${err.message || 'Unknown error'}`,
          );
        }
      }
    },

    // ─── Batch Item Edits ───

    /**
     * Record a user action on a batch item via the MAV API (action_type format).
     * Fire-and-forget: failures are logged but don't block the UI flow.
     *
     * @param {Object} item - The batch result item (must have .index)
     * @param {string} actionType - One of: parent_created, brand_created, channel_created, hierarchy_created, prediction_accepted, prediction_override, rule_created
     * @param {Object} data - Action-specific fields (flat, merged into request body)
     */
    recordAction(item, actionType, data = {}) {
      if (!this.activeBatchId || typeof item.index !== 'number') return;

      recordBatchItemEdit(this.activeBatchId, item.index, actionType, data)
        .then(edit => {
          if (!item.edits) {
            this.$set(item, 'edits', []);
          }
          const existingIdx = item.edits.findIndex(
            e => e.edit_type === actionType || e.action_type === actionType,
          );
          if (existingIdx !== -1) {
            this.$set(item.edits, existingIdx, edit);
          } else {
            item.edits.push(edit);
          }
        })
        .catch(err => {
          console.warn(`[MavBatchDialog] Failed to record ${actionType} action:`, err.message);
        });
    },

    /**
     * Hydrate local result items with edits already present in the batch status response.
     * Sets UI flags (_proposalCreated, _channelCreated, etc.) so buttons reflect prior actions.
     */
    hydrateEditsFromResults(items) {
      items.forEach(item => {
        if (!item.edits || !item.edits.length) return;

        for (const edit of item.edits) {
          switch (edit.edit_type) {
            case 'prediction_accepted':
              // User accepted the prediction and created a proposal
              this.$set(item, '_proposalCreated', true);
              if (edit.proposal_status?.proposal_id) {
                this.$set(item, '_proposalId', edit.proposal_status.proposal_id);
              } else if (edit.metadata?.proposal_id) {
                this.$set(item, '_proposalId', edit.metadata.proposal_id);
              }
              break;
            case 'prediction_override':
              // User overrode the AI prediction with a manually selected hierarchy
              this.$set(item, '_predictionOverridden', true);
              if (edit.metadata && item.prediction) {
                if (edit.metadata.parent_id) {
                  this.$set(item.prediction, 'parent_id', edit.metadata.parent_id);
                }
                if (edit.metadata.parent_name) {
                  this.$set(item.prediction, 'parent_name', edit.metadata.parent_name);
                }
                if (edit.metadata.brand_id) {
                  this.$set(item.prediction, 'brand_id', edit.metadata.brand_id);
                }
                if (edit.metadata.brand_name) {
                  this.$set(item.prediction, 'brand_name', edit.metadata.brand_name);
                }
                if (edit.metadata.channel_id) {
                  this.$set(item.prediction, 'channel_id', edit.metadata.channel_id);
                }
                if (edit.metadata.channel_name) {
                  this.$set(item.prediction, 'channel_name', edit.metadata.channel_name);
                }
              }
              break;
            case 'rule_created':
            case 'proposal_submitted':
              this.$set(item, '_proposalCreated', true);
              this.$set(item, '_ruleCreated', true);
              // Check proposal_status first, then metadata, then legacy proposal_id
              if (edit.proposal_status?.rule_id) {
                this.$set(item, '_ruleId', edit.proposal_status.rule_id);
              } else if (edit.metadata?.rule_id) {
                this.$set(item, '_ruleId', edit.metadata.rule_id);
              }
              if (edit.proposal_status?.proposal_id) {
                this.$set(item, '_proposalId', edit.proposal_status.proposal_id);
              } else if (edit.metadata?.proposal_id) {
                this.$set(item, '_proposalId', edit.metadata.proposal_id);
              } else if (edit.proposal_id) {
                this.$set(item, '_proposalId', edit.proposal_id);
              }
              break;
            case 'channel_created':
              // Check proposal_status for status
              if (edit.proposal_status) {
                const status = edit.proposal_status.status;
                if (status === 'approved') {
                  this.$set(item, '_channelCreated', true);
                } else if (status === 'pending') {
                  this.$set(item, '_channelProposalPending', true);
                }
              } else {
                // Legacy behavior - check metadata
                this.$set(item, '_channelCreated', true);
                if (edit.metadata?.proposal_status === 'pending') {
                  this.$set(item, '_channelProposalPending', true);
                }
              }

              if (edit.metadata && item.prediction) {
                if (edit.metadata.channel_id) {
                  this.$set(item.prediction, 'channel_id', edit.metadata.channel_id);
                }
                if (edit.metadata.channel_name) {
                  this.$set(item.prediction, 'channel_name', edit.metadata.channel_name);
                }
                if (!edit.proposal_status || edit.proposal_status.status === 'approved') {
                  // Clear suggestion flag if approved
                  this.$set(item.prediction, 'is_channel_suggestion', false);
                }
              }
              break;
            case 'brand_created':
              // Check proposal_status for status
              if (edit.proposal_status) {
                const status = edit.proposal_status.status;
                if (status === 'approved') {
                  this.$set(item, '_brandCreated', true);
                } else if (status === 'pending') {
                  this.$set(item, '_brandProposalPending', true);
                }
              } else {
                // Legacy behavior
                this.$set(item, '_brandCreated', true);
              }

              if (edit.metadata && item.prediction) {
                if (edit.metadata.brand_id) {
                  this.$set(item.prediction, 'brand_id', edit.metadata.brand_id);
                }
                if (edit.metadata.brand_name) {
                  this.$set(item.prediction, 'brand_name', edit.metadata.brand_name);
                }
              }
              break;
            case 'tree_created':
            case 'hierarchy_created':
              // Check proposal_status for status
              if (edit.proposal_status) {
                const status = edit.proposal_status.status;
                if (status === 'approved') {
                  this.$set(item, '_treeCreated', true);
                } else if (status === 'pending') {
                  this.$set(item, '_treeProposalPending', true);
                }
              } else {
                // Legacy behavior
                this.$set(item, '_treeCreated', true);
              }

              if (edit.metadata && item.prediction) {
                if (edit.metadata.parent_id) {
                  this.$set(item.prediction, 'parent_id', edit.metadata.parent_id);
                }
                if (edit.metadata.parent_name) {
                  this.$set(item.prediction, 'parent_name', edit.metadata.parent_name);
                }
                if (edit.metadata.brand_id) {
                  this.$set(item.prediction, 'brand_id', edit.metadata.brand_id);
                }
                if (edit.metadata.brand_name) {
                  this.$set(item.prediction, 'brand_name', edit.metadata.brand_name);
                }
                if (edit.metadata.channel_id) {
                  this.$set(item.prediction, 'channel_id', edit.metadata.channel_id);
                }
                if (edit.metadata.channel_name) {
                  this.$set(item.prediction, 'channel_name', edit.metadata.channel_name);
                }
                // Clear suggestion flags if approved
                if (!edit.proposal_status || edit.proposal_status.status === 'approved') {
                  this.$set(item.prediction, 'is_parent_suggestion', false);
                  this.$set(item.prediction, 'is_channel_suggestion', false);
                }
              }
              break;
          }
        }
      });
    },

    _resolveTreeId(prediction) {
      if (
        prediction.channel_id &&
        Number.isFinite(prediction.channel_id) &&
        prediction.channel_id > 0
      ) {
        return prediction.channel_id;
      }
      if (prediction.brand_id && Number.isFinite(prediction.brand_id) && prediction.brand_id > 0) {
        return prediction.brand_id;
      }
      if (
        prediction.parent_id &&
        Number.isFinite(prediction.parent_id) &&
        prediction.parent_id > 0
      ) {
        return prediction.parent_id;
      }
      return null;
    },

    _buildMultipleRulesPayload(item) {
      const prediction = item.prediction;
      const isInvalid = this.isInvalidPrediction(item);
      const tree_id = this._resolveTreeId(prediction);

      if (isInvalid) {
        const rule = {
          pattern: item.domain,
          effective_start_date: null,
          tree_id: null,
          localization: '*',
        };
        return {
          tree_id,
          isInvalid,
          endpoint: 'multiplerules/invalid',
          payload: { rules: [rule] },
        };
      }

      const rule = {
        pattern: item.domain,
        localization: '*',
        effective_start_date: null,
        created_via_mav: true,
      };
      if (tree_id) rule.tree_id = tree_id;
      return {
        tree_id,
        isInvalid,
        endpoint: 'multiplerules',
        payload: { rules: [rule] },
      };
    },

    // ─── Prediction Insights ───

    toggleInsight(index) {
      this.insightOpenIndex = this.insightOpenIndex === index ? -1 : index;
    },

    confidenceLabel(c) {
      const pct = Math.round(c * 100);
      if (c >= 0.9) return `${pct}% High`;
      if (c >= 0.7) return `${pct}% Moderate`;
      if (c >= 0.5) return `${pct}% Low`;
      return `${pct}% Very Low`;
    },

    /**
     * Minimal markdown-to-HTML renderer for prediction insights.
     * Handles: ### headings, **bold**, bullet lists, blockquotes, newlines.
     * No external dependency needed.
     */
    renderInsightMarkdown(text) {
      if (!text) return '';
      return text
        .split('\n')
        .map(line => {
          // Headings
          if (line.startsWith('### '))
            return `<h4 class="mav-insight-h">${this.escapeAndBold(line.slice(4))}</h4>`;
          // Blockquote
          if (line.startsWith('> '))
            return `<blockquote class="mav-insight-quote">${this.escapeAndBold(
              line.slice(2),
            )}</blockquote>`;
          // Bullet list item
          if (line.startsWith('- '))
            return `<div class="mav-insight-bullet">• ${this.escapeAndBold(line.slice(2))}</div>`;
          // Empty line → paragraph break
          if (line.trim() === '') return '<div class="mav-insight-spacer"></div>';
          // Normal text
          return `<p class="mav-insight-p">${this.escapeAndBold(line)}</p>`;
        })
        .join('');
    },

    escapeAndBold(text) {
      // Escape HTML entities, then convert **bold** markdown to <strong>
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    },

    getConfigMap,
  },
};
</script>

<style scoped>
/deep/ .mav-batch-dialog-shell {
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5) !important;
  background-color: transparent !important;
  overflow: visible !important;
}

.mav-batch-dialog {
  background: #394556;
  color: #eceff1;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

/* Header */
.mav-batch-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #394556;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.mav-batch-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}
.mav-batch-header-text {
  flex: 1;
  min-width: 0;
}
.mav-batch-header-title {
  color: #eceff1;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}
.mav-batch-header-sub {
  color: #78909c;
  font-size: 12px;
  margin-top: 2px;
}

/* Body (scrollable area beneath the pinned toolbar) */
.mav-batch-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: #394556;
  min-height: 0;
}

/* Toolbar — pinned at top of input view, outside the scroll area */
.mav-batch-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #394556;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.mav-batch-toolbar-label {
  color: #78909c;
  font-size: 12px;
  margin-right: 4px;
}
.mav-batch-default-locale {
  max-width: 80px;
  flex: 0 0 auto;
}
.mav-batch-default-locale /deep/ .v-input__slot {
  min-height: 28px !important;
  padding: 0 6px !important;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.15) !important;
}
.mav-batch-default-locale /deep/ .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.28);
}
.mav-batch-default-locale /deep/ .v-select__selection {
  font-size: 12px;
  color: #eceff1;
}
.mav-batch-count {
  color: #b0bec5;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.mav-batch-count strong {
  color: #eceff1;
}

/* List */
.mav-batch-list {
  display: flex;
  flex-direction: column;
}
.mav-batch-list-header,
.mav-batch-row {
  display: grid;
  grid-template-columns: 44px 1fr 320px 40px;
  align-items: center;
  padding: 0 20px;
  gap: 12px;
}
.mav-batch-list-header {
  height: 36px;
  color: #78909c;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
}
.mav-batch-row {
  height: 44px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 0.12s;
}
.mav-batch-row:hover {
  background: rgba(255, 255, 255, 0.02);
}
.mav-batch-row--failed {
  background: rgba(244, 67, 54, 0.04);
}
.mav-batch-row--failed:hover {
  background: rgba(244, 67, 54, 0.06);
}

/* Columns */
.col-num {
  color: #546e7a;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.col-domain {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.col-locales {
  min-width: 0;
}
.col-action {
  text-align: right;
}
.mav-domain-input {
  width: 100%;
  height: 28px;
  padding: 0 8px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  color: #eceff1;
  font: inherit;
  font-size: 13px;
  outline: none;
  transition: background 0.12s, border-color 0.12s;
}
.mav-domain-input::placeholder {
  color: #546e7a;
}
.mav-domain-input:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
.mav-domain-input:focus {
  background: rgba(102, 126, 234, 0.06);
  border-color: rgba(102, 126, 234, 0.4);
}

/* Read-only domain link used in progress/results views */
.mav-domain-link {
  color: #eceff1;
  text-decoration: none;
  font-size: 13px;
}
.mav-domain-link:hover {
  color: #ce93d8;
  text-decoration: underline;
}

/* Redirect target shown next to the submitted domain when the server
   resolved it to a different host (e.g. edgeon-bandwidth.com → cdn77.com) */
.mav-resolved-arrow {
  color: #546e7a !important;
  margin: 0 4px;
  flex-shrink: 0;
}
.mav-resolved-domain {
  color: #b0bec5;
  font-size: 12px;
  text-decoration: none;
}
.mav-resolved-domain:hover {
  color: #ce93d8;
  text-decoration: underline;
}

/* Small annotation for validation_result failures (DNS / HTTP 4xx / 5xx) */
.mav-validation-note {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 6px;
  font-size: 11px;
  color: #ef9a9a;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 3px;
  white-space: nowrap;
}

/* Locale button — single-line chip row */
.mav-locale-button {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 28px;
  padding: 0 8px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  font: inherit;
  color: inherit;
  text-align: left;
}
.mav-locale-button:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
.mav-locale-chip {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  background: rgba(206, 147, 216, 0.18);
  color: #e1bee7;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.mav-locale-chip + .mav-locale-chip {
  margin-left: 4px;
}
.mav-locale-empty {
  color: #78909c;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex: 1;
}
.mav-locale-default-tag {
  color: #546e7a;
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  margin-left: 4px;
}
.mav-locale-caret {
  margin-left: auto;
  color: #546e7a;
  flex-shrink: 0;
}

/* Locale editor menu */
.mav-locale-menu {
  padding: 14px;
  background: #1e1e1e !important;
  min-width: 280px;
}
.mav-locale-menu-title {
  color: #b0bec5;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
}
.mav-locale-menu-hint {
  color: #78909c;
  font-size: 11px;
  margin-top: 6px;
}

/* Empty state */
.mav-batch-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #546e7a;
  font-size: 13px;
  gap: 4px;
}
.mav-batch-empty-icon {
  color: rgba(255, 255, 255, 0.12) !important;
  margin-bottom: 8px;
}

/* Resuming indicator */
.mav-batch-resuming {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #b0bec5;
  font-size: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

/* Recent batches collapsible dropdown */
.mav-batch-recent {
  padding: 8px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mav-batch-recent-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 4px 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #b0bec5;
  font: inherit;
  text-align: left;
}
.mav-batch-recent-toggle:hover {
  color: #eceff1;
}
.mav-batch-recent-title {
  color: inherit;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.mav-batch-recent-list {
  margin-top: 6px;
}
.mav-batch-recent-row {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s;
}
.mav-batch-recent-row:hover {
  background: rgba(255, 255, 255, 0.04);
}
.mav-batch-recent-meta {
  color: #b0bec5;
  font-size: 12px;
}
.mav-batch-recent-time {
  color: #546e7a;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

/* ─── Progress view ─── */
.mav-batch-status-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.mav-batch-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #b0bec5;
}
.mav-batch-stats strong {
  color: #eceff1;
}

/* Progress view columns */
.mav-batch-list--progress .mav-batch-list-header,
.mav-batch-list--progress .mav-batch-row {
  grid-template-columns: 44px minmax(140px, 1.2fr) 120px minmax(220px, 2fr) minmax(120px, 140px);
}
.mav-batch-list--progress .mav-batch-row {
  height: auto;
  min-height: 56px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.col-status {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #b0bec5;
}
.col-prediction {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
.col-time {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.mav-prediction-name {
  color: #eceff1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Results view columns */
.mav-batch-list--results .mav-batch-list-header,
.mav-batch-list--results .mav-batch-row {
  grid-template-columns: 36px minmax(140px, 1fr) minmax(240px, 2fr) 70px minmax(160px, 180px);
}
.mav-batch-list--results .mav-batch-row {
  height: auto;
  min-height: 56px;
  padding-top: 8px;
  padding-bottom: 8px;
}
.mav-batch-row--selected {
  background: rgba(102, 126, 234, 0.06) !important;
}
.col-select {
  display: flex;
  align-items: center;
  justify-content: center;
}
.col-hierarchy {
  font-size: 12px;
  color: #b0bec5;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
}
.mav-hier-node {
  color: #b0bec5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.mav-hier-node--leaf {
  color: #eceff1;
  font-weight: 500;
}
.mav-hier-sep {
  color: #546e7a !important;
  flex-shrink: 0;
}
.col-conf,
.col-row-action {
  text-align: right;
}
.col-row-action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  justify-content: center;
}

/* ─── Footer ─── */
.mav-batch-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #2f3945;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.mav-selection-count {
  color: #ce93d8;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
}
.mav-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3) !important;
  letter-spacing: 0.02em !important;
}
.mav-btn-propose {
  background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%) !important;
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.3) !important;
  letter-spacing: 0.02em !important;
}
.mav-btn-magenta {
  background: linear-gradient(135deg, #d81b60 0%, #c2185b 100%) !important;
  box-shadow: 0 2px 6px rgba(216, 27, 96, 0.3) !important;
  letter-spacing: 0.02em !important;
  color: #ffffff !important;
  overflow: hidden;
}
.mav-btn-magenta.v-size--x-small /deep/ .v-btn__loader .v-progress-circular {
  height: 14px !important;
  width: 14px !important;
}
.mav-btn-magenta.v-size--x-small /deep/ .v-btn__loader .v-progress-circular svg {
  stroke-width: 3;
}

/* Filter toggles (results header) */
.mav-filter-toggle {
  flex-shrink: 0;
}
.mav-filter-toggle /deep/ .v-input--selection-controls__input {
  margin-right: 4px;
}
.mav-filter-label {
  color: #b0bec5;
  font-size: 11px;
  white-space: nowrap;
}

/* Status text in results */
.mav-status-text {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  line-height: 1.2;
}

.mav-proposal-link {
  text-decoration: none;
  cursor: pointer;
}

.mav-proposal-link:hover {
  text-decoration: underline;
}

/* Tree Creation Dialog */
.mav-tree-creation-card {
  background: #394556 !important;
  color: #eceff1;
}
.mav-tree-creation-header {
  background: #2f3945;
  padding: 24px 24px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
  color: #eceff1 !important;
}
.mav-tree-creation-body {
  padding: 16px 24px 24px !important;
}
.mav-tree-creation-card .v-card__actions {
  padding: 12px 16px !important;
  justify-content: flex-end;
}
.mav-tree-creation-card .v-card__actions .v-btn {
  margin-left: 8px;
  letter-spacing: 0.5px;
}
.mav-tree-creation-flow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.mav-tree-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  transition: all 0.2s;
}
.mav-tree-step--active {
  background: rgba(102, 126, 234, 0.08);
  border-color: rgba(102, 126, 234, 0.3);
}
.mav-tree-step--completed {
  background: rgba(67, 160, 71, 0.08);
  border-color: rgba(67, 160, 71, 0.3);
}
.mav-tree-step-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
  flex-shrink: 0;
}
.mav-tree-step-number {
  color: #b0bec5;
  font-size: 13px;
  font-weight: 600;
}
.mav-tree-step-content {
  flex: 1;
  min-width: 0;
}
.mav-tree-step-label {
  color: #78909c;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
}
.mav-tree-step-value {
  color: #eceff1;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mav-tree-arrow {
  color: #546e7a !important;
  flex-shrink: 0;
}
.mav-tree-status {
  display: flex;
  align-items: center;
  padding: 12px;
  background: rgba(102, 126, 234, 0.06);
  border-radius: 4px;
  color: #b0bec5;
  font-size: 13px;
}

/* ─── Prediction Insights ─── */
.mav-insight-btn {
  font-size: 11px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding: 0 6px !important;
  min-width: 0 !important;
  height: 24px !important;
}
.mav-insight-panel {
  grid-column: 1 / -1;
  background: #2a3444;
  border: 1px solid rgba(206, 147, 216, 0.25);
  border-left: 4px solid #ce93d8;
  border-radius: 8px;
  margin: 4px 20px 16px 20px;
  padding: 20px 24px;
}
.mav-insight-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.mav-insight-panel-title {
  color: #ce93d8;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.mav-insight-panel-body {
  color: #cfd8dc;
  font-size: 13px;
  line-height: 1.7;
}
.mav-insight-panel-body /deep/ .mav-insight-h {
  color: #e1bee7;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 20px 0 10px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mav-insight-panel-body /deep/ .mav-insight-h:first-child {
  margin-top: 0;
}
.mav-insight-panel-body /deep/ .mav-insight-p {
  margin: 0 0 6px 0;
  color: #b0bec5;
}
.mav-insight-panel-body /deep/ .mav-insight-bullet {
  padding-left: 16px;
  margin: 4px 0;
  position: relative;
  color: #b0bec5;
}
.mav-insight-panel-body /deep/ .mav-insight-quote {
  border-left: 3px solid rgba(206, 147, 216, 0.5);
  padding: 8px 16px;
  margin: 10px 0;
  color: #90a4ae;
  font-style: italic;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 0 6px 6px 0;
}
.mav-insight-panel-body /deep/ strong {
  color: #ffffff;
  font-weight: 600;
}
.mav-insight-panel-body /deep/ .mav-insight-spacer {
  height: 8px;
}

/* Insight slide transition */
.mav-insight-slide-enter-active,
.mav-insight-slide-leave-active {
  transition: all 0.2s ease;
  max-height: 500px;
}
.mav-insight-slide-enter,
.mav-insight-slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}
</style>
